<script>
  // Карточка этапа в ленте (этап 23): название, «N сообщений · даты», конспект
  // markdown-рендером, состояния pending/failed, хвосты с кнопкой «В композер».
  // Тап по карточке открывает шторку действий (как у сообщения); ссылки-источники
  // внутри конспекта ведут к сообщению через action 'open-source'.
  import { createEventDispatcher } from 'svelte';
  import { longpress } from '../actions/longpress.js';
  import { renderMarkdown } from '../markdown.js';
  import { TEXT } from '../ui-text.js';
  import { formatStageRange, stageTitle } from '../chat-stages.js';
  import { formatTime } from '../publication.js';

  export let stage;
  export let summary = null; // сообщение role='summary' или null (pending/failed)
  export let number = null;
  export let isOwner = false;
  // Этап 26.4: когда конспект последний раз поправили в git (опубликованный чат).
  export let gitImportedAt = null;

  const dispatch = createEventDispatcher();

  let carryOpen = false;

  $: title = stageTitle(stage, number, TEXT.stages.cardTitle);
  $: status = stage?.status || 'pending';
  $: hidden = Boolean(summary?.summaryHidden);
  $: text = hidden ? '' : (summary?.text || '');
  $: rendered = text ? renderMarkdown(text) : '';
  $: meta = TEXT.stages.cardMeta
    .replace('{count}', String(stage?.messageCount ?? 0))
    .replace('{range}', formatStageRange(stage));
  $: carryOver = Array.isArray(stage?.carryOver) ? stage.carryOver : [];

  function act(type, extra = {}) {
    dispatch('action', { type, stage, summary, ...extra });
  }

  function handleClick(event) {
    const source = event.target.closest('a[data-source-message]');
    if (source) {
      event.preventDefault();
      act('open-source', { messageId: source.getAttribute('data-source-message') });
      return;
    }
    if (event.target.closest('a, button')) {
      return;
    }
    act('open-actions');
  }
</script>

<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
<div
  class="stage-card"
  class:pending={status === 'pending'}
  class:failed={status === 'failed'}
  data-stage-id={stage?.stageId}
  data-testid="stage-card"
  use:longpress
  on:longpress={() => act('open-actions')}
  on:click={handleClick}
>
  <div class="stage-head">
    <span class="stage-badge">{TEXT.stages.cardTitle}{number ? ` ${number}` : ''}</span>
    <span class="stage-title">{stage?.title ? stage.title : ''}</span>
  </div>
  <div class="stage-meta">{meta}</div>
  {#if gitImportedAt}
    <div class="stage-git" data-testid="stage-git-mark">{TEXT.publish.importedMark.replace('{time}', formatTime(gitImportedAt))}</div>
  {/if}

  {#if status === 'pending'}
    <div class="stage-status" role="status">
      <span class="spinner small" aria-hidden="true"></span>
      <span>{TEXT.stages.pending}</span>
    </div>
  {:else if status === 'failed'}
    <div class="stage-status error" role="alert">
      {TEXT.stages.failed.replace('{error}', stage?.error || '')}
    </div>
    {#if isOwner}
      <div class="stage-actions">
        <button type="button" class="stage-btn" on:click={() => act('regenerate')}>{TEXT.stages.retry}</button>
        <button type="button" class="stage-btn danger" on:click={() => act('reopen')}>{TEXT.stages.reopen}</button>
      </div>
    {/if}
  {:else if hidden}
    <p class="stage-hidden">{TEXT.stages.hiddenSummary}</p>
  {:else}
    <div class="stage-summary markdown-body">
      {@html rendered}
    </div>
    {#if carryOver.length}
      <div class="carry-over">
        <button type="button" class="carry-toggle" aria-expanded={carryOpen} on:click={() => (carryOpen = !carryOpen)}>
          {TEXT.stages.carryOverTitle} ({carryOver.length}) {carryOpen ? '▴' : '▾'}
        </button>
        {#if carryOpen}
          <ul class="carry-list">
            {#each carryOver as item, index (index)}
              <li>{item}</li>
            {/each}
          </ul>
          {#if isOwner}
            <button type="button" class="stage-btn" on:click={() => act('carry-over')}>{TEXT.stages.carryOverToComposer}</button>
          {/if}
        {/if}
      </div>
    {/if}
  {/if}
</div>

<style>
  .stage-git {
    font-size: 0.75rem;
    color: var(--accent-color);
    margin-top: 0.15rem;
  }

  .stage-card {
    align-self: stretch;
    margin-bottom: var(--spacing-md);
    padding: 0.75rem 1rem;
    border-radius: 14px;
    border: 1px solid color-mix(in srgb, var(--accent-color) 45%, var(--border-color));
    background: color-mix(in srgb, var(--accent-color) 7%, var(--bg-secondary));
    color: var(--text-primary);
    line-height: 1.45;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
  }

  .stage-card.pending {
    border-style: dashed;
  }

  .stage-card.failed {
    border-color: var(--error-color, #d9534f);
  }

  .stage-head {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .stage-badge {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--accent-color);
  }

  .stage-title {
    font-weight: 600;
  }

  .stage-meta {
    margin-top: 0.15rem;
    font-size: 0.75rem;
    color: var(--text-secondary);
  }

  .stage-status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    font-size: 0.9rem;
    color: var(--text-secondary);
  }

  .stage-status.error {
    color: var(--error-color, #d9534f);
  }

  .stage-summary {
    margin-top: 0.5rem;
    font-size: 0.95rem;
  }

  .stage-summary :global(h2),
  .stage-summary :global(h3) {
    font-size: 0.95rem;
    margin: 0.6rem 0 0.2rem;
  }

  .stage-summary :global(a.source-link) {
    color: var(--accent-color);
    text-decoration: none;
    font-size: 0.75rem;
    vertical-align: super;
    cursor: pointer;
  }

  .stage-hidden {
    margin-top: 0.5rem;
    font-style: italic;
    color: var(--text-secondary);
  }

  .carry-over {
    margin-top: 0.5rem;
    border-top: 1px dashed var(--border-color);
    padding-top: 0.4rem;
  }

  .carry-toggle {
    background: none;
    border: none;
    padding: 0;
    color: var(--text-secondary);
    font-size: 0.8rem;
    cursor: pointer;
  }

  .carry-list {
    margin: 0.3rem 0 0.4rem;
    padding-left: 1.1rem;
    font-size: 0.9rem;
  }

  .stage-actions {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.5rem;
    flex-wrap: wrap;
  }

  .stage-btn {
    padding: 0.35rem 0.75rem;
    border-radius: 999px;
    border: 1px solid var(--accent-color);
    background: var(--bg-primary);
    color: var(--accent-color);
    font-size: 0.85rem;
    cursor: pointer;
  }

  .stage-btn.danger {
    border-color: var(--error-color, #d9534f);
    color: var(--error-color, #d9534f);
  }
</style>
